import { reactive, Ref, ComputedRef } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { markRaw } from 'vue'
import { apiService } from '@/services'
import DistributePackageModal from '@/components/content/packages/my-packages/addpackage/DistributePackageModal.vue'
import PackageCreatedIcon from '@/assets/popups/create-package.svg'
import AlertTriangleIcon from '@/assets/popups/alert.svg'
import { ModalType, hideMainAndOverlyModals, initAndRevealMainModal } from '@/components/modals/modals-util'
import type { IModalProps } from '@/store/custom-modal.module'
import { CustomModalProps } from '@/interfaces'
import { HTTP_STATUS_BAD_REQUEST } from '@/services/constants'
import { SelectedCoverImage, PostPackagePayload } from '@/services/content/package/types'
import { extractCoverImagePath, defaultPackageCoverOptionsMap } from '@/components/content/packages/packageCoversUtils'

interface DistributionModalState {
  autoMatch: boolean
  selectedOnly: boolean
  emails: string[]
  isLoading: boolean
  errors: {
    selectedOnly: string
    autoMatch: string
    general: string
    emails: string
  }
}

interface PackageForm {
  packageName: Ref<string>
  packageDescription: Ref<string>
  packageTags: Ref<Array<{ value: string } | string>>
  selectedVideos: ComputedRef<Array<{ video_id: string }>>
  selectedFolders: ComputedRef<Array<{ id: number }>>
  selectedCoverImage: Ref<SelectedCoverImage | null>
  uploadedCoverImages: Ref<Record<string, string>>
}

interface PackageDistributionParams {
  editMode: ComputedRef<boolean>
  newPackageId: Ref<number | undefined>
  packageForm: PackageForm
  updatePackage: () => Promise<void>
  isEdited: () => boolean
  packageId?: Ref<number | undefined>
  onSuccess?: () => void
}

interface HttpErrorResponse {
  response: {
    status: number
    data?: {
      error?: {
        code?: string
      }
    }
  }
}

export function usePackageDistribution({ editMode, newPackageId, packageForm, updatePackage, isEdited, packageId, onSuccess }: PackageDistributionParams) {
  const { packageName, packageDescription, packageTags, selectedVideos, selectedFolders, selectedCoverImage, uploadedCoverImages } = packageForm
  const store = useStore()
  const { t } = useI18n()

  const failureStates = reactive({
    autoMatchFailed: false,
    selectedOnlyFailed: false
  })

  async function createPackageIfNeeded() {
    if (packageId?.value) return

    if (!editMode.value && !newPackageId.value) {
      await createNewPackage()
    } else if (editMode.value && isEdited()) {
      await updatePackage()
    }
  }

  function getCoverUrlFromSelectedImage(): string | null {
    const selected = selectedCoverImage?.value
    if (!selected || !selected.image) return null

    // CASE 1: Default cover image — match image to type
    const matchedEntry = Object.entries(defaultPackageCoverOptionsMap).find(([, image]) => image === selected.image)
    if (matchedEntry) {
      return matchedEntry[0]
    }

    // CASE 2: Uploaded image with blob URL (local object URL)
    const proto = selected.image.split(':')[0]
    if (proto === 'blob') {
      const fullUrl = uploadedCoverImages?.value?.[selected.image]
      return extractCoverImagePath(fullUrl)
    }

    // CASE 3: Saved image URL from backend
    if (proto === 'http' || proto === 'https') {
      return extractCoverImagePath(selected.image)
    }

    return null
  }

  async function createNewPackage() {
    try {
      const videosIds = selectedVideos.value.map((v) => v.video_id)
      const foldersIds = selectedFolders.value.map((f) => f.id)
      const coverUrl = getCoverUrlFromSelectedImage()
      const uploadedImagePaths = uploadedCoverImages?.value
        ? (Object.values(uploadedCoverImages.value)
            .map((url) => extractCoverImagePath(url))
            .filter(Boolean) as string[])
        : []

      const payload: PostPackagePayload = {
        package_name: packageName.value,
        description: packageDescription.value,
        video_ids: videosIds,
        folder_ids: foldersIds,
        cover_image: coverUrl || '',
        other_cover_images_paths: uploadedImagePaths.length > 0 ? uploadedImagePaths : null,
        tags: packageTags.value?.map((tag: { value: string } | string) => (typeof tag === 'string' ? tag : tag.value)) || []
      }

      const response = await apiService.contentPackage.postPackage(payload)

      newPackageId.value = response.package_id
      store.dispatch('messages/addMessage', {
        message: t(`content-package-${editMode.value ? 'edit' : 'add'}-success`),
        type: 'success'
      })
    } catch (err) {
      store.dispatch('messages/addMessage', {
        message: t('content-package-add-error'),
        type: 'error'
      })
      throw err
    }
  }

  function createModalState(): DistributionModalState {
    return reactive({
      autoMatch: false,
      selectedOnly: false,
      emails: [] as string[],
      isLoading: false,
      errors: {
        selectedOnly: '',
        autoMatch: '',
        general: '',
        emails: ''
      }
    })
  }

  function handleDistributionError(err: HttpErrorResponse, modalState: DistributionModalState, errorType: 'selectedOnly' | 'autoMatch') {
    if (err && typeof err === 'object' && 'response' in err) {
      const error = err
      if (error.response && error.response.status === HTTP_STATUS_BAD_REQUEST) {
        if (error.response.data?.error?.code === 'SERVICE_PACKAGES_13') {
          modalState.errors[errorType] = t('package-distribution-error-cant-send-to-own-org')
        } else {
          const bothSelected = modalState.autoMatch && modalState.selectedOnly
          if (!bothSelected) {
            modalState.errors[errorType] = t('reached-limit-error')
          }
        }

        if (errorType === 'selectedOnly') {
          failureStates.selectedOnlyFailed = true
        } else {
          failureStates.autoMatchFailed = true
        }
      }
    }
  }

  async function handleDistributionCancel() {
    hideMainAndOverlyModals()
    if (onSuccess) {
      onSuccess()
    }
  }

  async function processAutoMatchDistribution(modalState: DistributionModalState) {
    if (!modalState.autoMatch || failureStates.autoMatchFailed) return

    try {
      modalState.isLoading = true
      await apiService.contentPackage.postContentProposal({
        package_id: packageId?.value || newPackageId.value,
        all_publishers: true
      })
      modalState.errors.autoMatch = ''
      failureStates.autoMatchFailed = false
    } catch (err) {
      handleDistributionError(err, modalState, 'autoMatch')
    } finally {
      modalState.isLoading = false
    }
  }

  async function processSelectedOnlyDistribution(modalState: DistributionModalState) {
    if (!modalState.selectedOnly || failureStates.selectedOnlyFailed) return

    type EmailTag = string | { value: string }
    const recipients = (modalState.emails || []).map((el: EmailTag) => (typeof el === 'string' ? el : (el.value ?? ''))).filter((email: string) => !!email)

    if (!recipients.length) return

    try {
      modalState.isLoading = true
      await apiService.contentPackage.postContentProposal({
        package_id: packageId?.value || newPackageId.value,
        all_publishers: false,
        recipients
      })
      modalState.errors.selectedOnly = ''
      failureStates.selectedOnlyFailed = false
    } catch (err) {
      handleDistributionError(err, modalState, 'selectedOnly')
    } finally {
      modalState.isLoading = false
    }
  }

  function handleDistributionResults(modalState: DistributionModalState) {
    const autoMatchSucceeded = modalState.autoMatch && !failureStates.autoMatchFailed
    const selectedOnlySucceeded = modalState.selectedOnly && !failureStates.selectedOnlyFailed
    const hasAnySuccess = autoMatchSucceeded || selectedOnlySucceeded
    const bothSelected = modalState.autoMatch && modalState.selectedOnly

    // Special case: both selected but one failed and one succeeded
    if (bothSelected && hasAnySuccess && (failureStates.autoMatchFailed || failureStates.selectedOnlyFailed)) {
      const headerText = failureStates.autoMatchFailed
        ? t('package-distribution-error-header-special-scenario-matched-publisher')
        : t('package-distribution-error-header-special-scenario-selected-publisher')
      const subHeaderText = failureStates.autoMatchFailed
        ? t('package-distribution-error-sub-header-special-scenario-matched-publisher')
        : t('package-distribution-error-sub-header-special-scenario-selected-publisher')

      OneFailedOnePassedErrorModal(headerText, subHeaderText)
      return
    }

    if (hasAnySuccess) {
      hideMainAndOverlyModals()
      if (onSuccess) {
        onSuccess()
      }
      store.dispatch('messages/addMessage', {
        message: t('package-distribution-success'),
        type: 'success'
      })
      return
    }

    // If both were selected and both failed, show error modal
    if (bothSelected && failureStates.autoMatchFailed && failureStates.selectedOnlyFailed) {
      showDistributionErrorModal()
      return
    }

    // If only one was selected and it failed, show individual error
    if (!bothSelected) {
      if (modalState.autoMatch && failureStates.autoMatchFailed) {
        if (!modalState.errors.autoMatch) {
          modalState.errors.autoMatch = t('reached-limit-error')
        }
      }
      if (modalState.selectedOnly && failureStates.selectedOnlyFailed) {
        if (!modalState.errors.selectedOnly) {
          modalState.errors.selectedOnly = t('reached-limit-error')
        }
      }
    }

    updateDistributionValidation()
  }

  function showDistributionErrorModal() {
    hideMainAndOverlyModals()
    failureStates.autoMatchFailed = false
    failureStates.selectedOnlyFailed = false

    const errorModalProps: CustomModalProps = {
      headerProps: {
        text: t('package-distribution-error-header'),
        textSecondary: t('package-distribution-error-sub-header'),
        icon: AlertTriangleIcon
      },
      footerProps: {
        buttonCancel: t('close'),
        buttonConfirm: '',
        isConfirmValid: false
      },
      showDiscardStep: false
    }

    const errorModal: IModalProps = {
      modalType: ModalType.CUSTOM_ALERT_MODAL,
      props: errorModalProps
    }

    initAndRevealMainModal(errorModal)
  }

  function OneFailedOnePassedErrorModal(headerText: string, subHeaderText: string) {
    hideMainAndOverlyModals()
    failureStates.autoMatchFailed = false
    failureStates.selectedOnlyFailed = false

    const errorModalProps: CustomModalProps = {
      headerProps: {
        text: headerText,
        textSecondary: subHeaderText,
        icon: AlertTriangleIcon
      },
      footerProps: {
        buttonCancel: t('close'),
        buttonConfirm: '',
        isConfirmValid: false
      },
      showDiscardStep: false
    }

    const errorModal: IModalProps = {
      modalType: ModalType.CUSTOM_ALERT_MODAL,
      props: errorModalProps
    }

    initAndRevealMainModal(errorModal)
  }

  async function handleDistributionConfirm(modalState: DistributionModalState) {
    if (!modalState.autoMatch && !modalState.selectedOnly) {
      modalState.errors.general = t('distribute-package-insert-error')
      updateDistributionValidation()
      return
    }

    modalState.errors.general = ''

    if (modalState.selectedOnly && (!modalState.emails || modalState.emails.length === 0)) {
      modalState.errors.emails = t('distribute-package-pub-email-error')
      updateDistributionValidation()
      return
    }

    if (modalState.selectedOnly && modalState.emails && modalState.emails.length > 0) {
      modalState.errors.emails = ''
    }

    await processAutoMatchDistribution(modalState)
    await processSelectedOnlyDistribution(modalState)
    updateDistributionValidation()
    handleDistributionResults(modalState)
  }

  function updateDistributionValidation() {
    store.dispatch('modal/setActionValidation', true)
  }

  function onDistributionModalStateUpdate(modalState: DistributionModalState, payload: { autoMatch: boolean; selectedOnly: boolean; emails: string[] }) {
    modalState.autoMatch = payload.autoMatch
    modalState.selectedOnly = payload.selectedOnly
    modalState.emails = payload.emails

    if (modalState.autoMatch || modalState.selectedOnly) {
      modalState.errors.general = ''
    }

    if (modalState.selectedOnly && modalState.emails && modalState.emails.length > 0) {
      modalState.errors.emails = ''
    }

    updateDistributionValidation()
  }

  async function openDistributionModal() {
    await createPackageIfNeeded()

    const modalState = createModalState()

    const modalProps: CustomModalProps = {
      headerProps: {
        text: t('package-created'),
        textSecondary: t('distribute-package-sub-header'),
        icon: PackageCreatedIcon
      },
      footerProps: {
        buttonCancel: t('skip'),
        buttonConfirm: t('content-package-share'),
        isConfirmValid: true
      },
      onDismiss: handleDistributionCancel,
      onConfirm: () => handleDistributionConfirm(modalState),
      showDiscardStep: false
    }

    updateDistributionValidation()

    const modal: IModalProps = {
      modalType: ModalType.DISTRIBUTE_PACKAGE_MODAL,
      componentContent: markRaw(DistributePackageModal),
      props: {
        ...modalProps,
        modalState,
        updateValidation: updateDistributionValidation,
        onUpdateModalState: (payload: { autoMatch: boolean; selectedOnly: boolean; emails: string[] }) => onDistributionModalStateUpdate(modalState, payload)
      } as CustomModalProps & {
        modalState: DistributionModalState
        updateValidation: () => void
        onUpdateModalState: (payload: { autoMatch: boolean; selectedOnly: boolean; emails: string[] }) => void
      }
    }

    initAndRevealMainModal(modal)
  }

  return { openDistributionModal }
}
